import {Requester} from '@getgo/g2r-shared-components';
import * as React from 'react';
import { API_URL } from '../Constants';
import {createLogger} from '../helpers/Logger';
import {Lang, polyglot} from '../helpers/Polyglot';
import {getHeaders} from '../helpers/Utils';

export interface AccountProps {
    key: string;
    name: string;
    adminRoles?:  string[];
    invitedDate?: number;
}

export interface CurrentUser {
    key: string;
    accountKey: string;
    email: string;
    firstName: string;
    lastName: string;
    locale: string;
    timeZone: string;
    adminRoles?: string[];
    systemRoles?: string[];
    accounts?: AccountProps[];
    createTime: number;
    products:  string[];
}

export interface SettingsKeeperState {
  language: string;
  timezone: string;
  timeFormat: string;
  currentUser?: CurrentUser;
  userError: boolean;
}

export const DefaultSettings: SettingsKeeperState = {
  language: (navigator.language).split('-')[0],
  timeFormat: (new Intl.DateTimeFormat('default',
    {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).format(new Date(2014, 1, 1, 15, 0, 0, 0)).indexOf('15') > -1) ? '24' : '12',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    currentUser: null,
    userError: false,
};

export const SettingsContext = React.createContext<SettingsKeeperState>(
  {
    language: DefaultSettings.language,
    timeFormat: DefaultSettings.timeFormat,
    timezone: DefaultSettings.timezone,
    currentUser: DefaultSettings.currentUser,
    userError: false,
  },
);

const logger = createLogger('SettingsKeeper');

export const SettingsKeeper = ({children}) => {
    const [timezone] = React.useState(DefaultSettings.timezone);
    const [timeFormat] = React.useState(DefaultSettings.timeFormat);
    const [language, setLanguage] = React.useState('');
    const [currentUser, setCurrentUser] = React.useState(DefaultSettings.currentUser);
    const [userError, setUserError] = React.useState(false);
    const userLocale = (locale: string): string =>  locale && locale.split('_').length === 2 ? locale.split('_')[0] : 'en';

    React.useEffect(() => {
        Requester.request(API_URL, `/ext-admin/rest/me`, {
            method: 'GET',
            headers: getHeaders(),
        }, logger).then((resp: CurrentUser) => {
            const locale = userLocale(resp.locale);
            Lang.setLanguage(locale);
            setCurrentUser(resp);
            setTimeout(() => { setLanguage(locale);}, 500);
        }).catch( (error) => {
          logger.info(`Error in fetching user details - ${JSON.stringify(error)}`);
          if (error.errors.some((e) => e.code === 'not.authenticated')) {
            localStorage.removeItem('accessToken');
            location.reload();
          } else {
            setUserError(true);
          }
        });
    }, []);

    return (
        <SettingsContext.Provider value={{language, timezone, timeFormat, currentUser, userError}}>
          {children}
        </SettingsContext.Provider>
    );
};
